import { apiPost } from '../../lib/api'
import { withAuth } from '../auth/token'

/** Mirrors the verification slice on the API. */
export type FmcsaOutcome =
  | 'Verified'
  | 'NotFound'
  | 'Ambiguous'
  | 'Unavailable'
  | 'NoIdentifier'

export type FmcsaAuthority = 'NotReported' | 'Active' | 'Inactive' | 'None'

export interface FmcsaCandidate {
  usdotNumber: string | null
  legalName: string | null
  city: string | null
  state: string | null
}

/** Something wrong with the record's MC number, found after the USDOT verified. */
export interface FmcsaNumberConflict {
  /** 'DifferentCarrier' — it belongs to someone else. 'NotListed' — nobody holds it. */
  kind: 'DifferentCarrier' | 'NotListed'
  legalName: string | null
  usdotNumber: string | null
}

export interface FmcsaVerification {
  outcome: FmcsaOutcome
  legalName: string | null
  dbaName: string | null
  usdotNumber: string | null
  allowedToOperate: boolean | null
  commonAuthority: FmcsaAuthority
  contractAuthority: FmcsaAuthority
  brokerAuthority: FmcsaAuthority
  physicalCity: string | null
  physicalState: string | null
  /** Null means nobody can tell — never render it as a failure. */
  nameMatches: boolean | null
  enteredName: string | null
  /** The place on file against the register's; null when either side is silent. */
  addressMatches: boolean | null
  enteredCity: string | null
  enteredState: string | null
  /**
   * Whether the register backs this record's role — broker authority for a broker,
   * operating authority for a carrier. 'NotReported' when it could not be judged.
   */
  authorityForRole: 'NotReported' | 'Held' | 'NotHeld'
  verifiedAt: string | null
  candidates: FmcsaCandidate[] | null
  reason: string | null
  mcNumberConflict: FmcsaNumberConflict | null
  /** False when the record moved under the lookup: a true answer, filed nowhere. */
  filed: boolean
}

export function verifyBroker(id: string): Promise<FmcsaVerification> {
  return apiPost<FmcsaVerification>(`/api/brokers/${id}/verify`, {}, { headers: withAuth() })
}

export function verifyCarrier(id: string): Promise<FmcsaVerification> {
  return apiPost<FmcsaVerification>(`/api/carriers/${id}/verify`, {}, { headers: withAuth() })
}
